/**
 * useTopicResearchRuns — Firestore real-time subscription to research runs
 * for a given topic. Single-doc model (Sprint 18.1): each run's envelope
 * already contains `brief`, `research`, `analysis` inline, so the hook just
 * emits the envelope docs — no separate subcollection loaders needed.
 *
 * Uses a single-field `where('topicId', '==', topicId)` query (no composite
 * index required). Sort is client-side by createdAt desc.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';

export interface UseTopicResearchRunsReturn {
  runs: any[];
  loading: boolean;
  error: Error | null;
}

export function useTopicResearchRuns(
  projectId: string | null,
  topicId: string | null,
  authorId?: string | null,
): UseTopicResearchRunsReturn {
  const [runs, setRuns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const db = useMemo(() => CoreFirebaseService.getInstance().firestore, []);

  useEffect(() => {
    if (!projectId || !topicId || authorId === 'all') {
      setRuns([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const constraints = [where('topicId', '==', topicId)];
    if (authorId) constraints.push(where('authorId', '==', authorId));
    const q = query(collection(db, 'projects', projectId, 'research_runs'), ...constraints);
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        rows.sort((a, b) => tsToMs(b.createdAt) - tsToMs(a.createdAt));
        setRuns(rows);
        setLoading(false);
      },
      (err) => {
        console.error('[useTopicResearchRuns] subscription error', err);
        setError(err as Error);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [db, projectId, topicId, authorId]);

  return { runs, loading, error };
}

function tsToMs(ts: any): number {
  if (!ts) return 0;
  if (typeof ts === 'string') return new Date(ts).getTime();
  if (typeof ts?.toMillis === 'function') return ts.toMillis();
  if (typeof ts?.seconds === 'number') return ts.seconds * 1000;
  return 0;
}
